import { useEffect, useMemo, useState } from "react";

const APP_UPDATE_CHECK_INTERVAL_MS = 60_000;

function getAssetSignature(doc: Document) {
  const scriptSources = Array.from(
    doc.querySelectorAll<HTMLScriptElement>("script[src]"),
  ).map((script) => `script:${script.getAttribute("src") ?? ""}`);
  const stylesheetHrefs = Array.from(
    doc.querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"][href]'),
  ).map((link) => `style:${link.getAttribute("href") ?? ""}`);

  return [...scriptSources, ...stylesheetHrefs].sort().join("|");
}

type RefreshAppBannerState = {
  isVisible: boolean;
  refresh: () => void;
};

function useRefreshAppBanner(): RefreshAppBannerState {
  const initialAssetSignature = useMemo(() => getAssetSignature(document), []);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const checkForAppUpdate = async () => {
      try {
        const response = await fetch(`/?_=${Date.now()}`, {
          cache: "no-store",
        });

        if (!response.ok) {
          return;
        }

        const html = await response.text();
        const nextDocument = document.implementation.createHTMLDocument("");
        nextDocument.documentElement.innerHTML = html;

        const nextAssetSignature = getAssetSignature(nextDocument);
        if (
          nextAssetSignature !== "" &&
          nextAssetSignature !== initialAssetSignature
        ) {
          setIsVisible(true);
        }
      } catch {
        // Connectivity problems are shown by the offline/technical banners.
      }
    };

    const updateCheckInterval = setInterval(
      checkForAppUpdate,
      APP_UPDATE_CHECK_INTERVAL_MS,
    );

    return () => clearInterval(updateCheckInterval);
  }, [initialAssetSignature]);

  return {
    isVisible,
    refresh: () => window.location.reload(),
  };
}

export { useRefreshAppBanner };
